import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import { leadMatches } from '../lib/leadSearch.js'
import { loadSentMail } from '../lib/sentMail.js'
import { outreachOf } from '../lib/companyMeta.js'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { PageHeader } from '../components/ui.jsx'
import CompanyTable, { keyOf } from '../components/CompanyTable.jsx'
import GeoPicker from '../components/GeoPicker.jsx'
import SyncButton from '../components/SyncButton.jsx'
import { FilterChip, CheckRow } from '../components/FilterChip.jsx'
import { SparklesIcon, PlusIcon, MailIcon, DownloadIcon } from '../components/icons.jsx'
import { MAX_ENRICH_ROWS, loadActiveBatch, isResearched } from '../lib/enrich.js'
import { runEnrichment, resumeEnrichment, enrichmentRunning } from '../lib/enrichRunner.js'
import { useManifest } from '../lib/data.jsx'
import { useLeadsCache } from '../lib/leadsCache.js'
import { buildGeoIndex, geoPredicate, geoCount, EMPTY_GEO, parseCityState } from '../lib/geo.js'
import {
  SIZE_BANDS,
  VOLUME_BANDS,
  WEBSITE_BANDS,
  sizeBandOf,
  volumeBandOf,
  websiteBandOf,
  certified,
  volumeDisclosed,
  projectMeta,
} from '../lib/companyMeta.js'
import { listViews, saveView, deleteView } from '../lib/savedViews.js'
import { filtersFromParams, filtersToParams } from '../lib/directoryFilters.js'
import { intentScoreOf } from '../lib/intentScore.js'
import { calibration } from '../lib/intel.js'
import { openCompose } from '../lib/compose.js'
import { enrichedSet, subscribeEnriched, enrichingSet, isRowEnriched } from '../lib/enrichedLeads.js'
import { reenrichState } from '../lib/enrichment.js'
import { listManualLeads, manualContributors } from '../lib/manualLeads.js'
import { useRbac } from '../lib/rbac.js'
import { hubspotUsers } from '../lib/hubspot.js'
import { usageStatusNote } from '../lib/leadUsage.js'

// One filter chip per firmographic column: multi-select bands, OR within a
// chip, AND across chips. `counts` are faceted: each chip's numbers come from
// the rows passing every other active filter, so they track the current view.
function BandChip({ label, options, values, counts, onToggle, onClear }) {
  return (
    <FilterChip label={label} count={values.length} active={values.length > 0} onClear={onClear}>
      {options.map((o) => (
        <CheckRow
          key={o.value}
          checked={values.includes(o.value)}
          onToggle={() => onToggle(o.value)}
          sub={(counts[o.value] || 0).toLocaleString()}
        >
          {o.label}
        </CheckRow>
      ))}
    </FilterChip>
  )
}

const EMPTY_BANDS = { size: [], volume: [], website: [], enriched: [] }

// Quality flags — three yes/no cuts that used to live only in saved segments,
// where nothing could act on them. AND-ed together, and with everything else.
const EMPTY_FLAGS = { certified: false, activeProjects: false, volumeDisclosed: false }

// Not a firmographic field — it's this account's enrichment marks OR the row's
// own `enriched` flag (imported leads arrive researched), so it lives here
// rather than in companyMeta's bands. isRowEnriched reads both.
const ENRICHED_BANDS = [
  { value: 'yes', label: 'Enriched' },
  { value: 'no', label: 'Not enriched' },
]

const splitTrades = (v) =>
  String(v || '')
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)

// Trade facet chip: the directory has hundreds of distinct trades, so the
// panel gets its own search box and a scrollable, count-ranked list.
function TradeChip({ options, values, onToggle, onClear }) {
  const [q, setQ] = useState('')
  const needle = q.trim().toLowerCase()
  const shown = (needle ? options.filter((o) => o.trade.toLowerCase().includes(needle)) : options).slice(0, 40)
  return (
    <FilterChip label="Industry" count={values.length} active={values.length > 0} onClear={onClear}>
      <input
        type="search"
        className="mb-1.5 w-full rounded-[8px] border border-line bg-panel px-2.5 py-[7px] text-[13px] text-ink placeholder:text-faint focus:border-primary focus:outline-none"
        placeholder="Search trades…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onClick={(e) => e.stopPropagation()}
      />
      <div className="max-h-[260px] overflow-y-auto">
        {shown.map((o) => (
          <CheckRow
            key={o.trade}
            checked={values.includes(o.trade)}
            onToggle={() => onToggle(o.trade)}
            sub={o.count.toLocaleString()}
          >
            {o.trade}
          </CheckRow>
        ))}
        {!shown.length && <div className="px-2.5 py-2 text-[12.5px] text-faint">No trades match.</div>}
        {!needle && options.length > 40 && (
          <div className="px-2.5 py-1.5 text-[11.5px] text-faint">
            Top 40 of {options.length} — search to find the rest
          </div>
        )}
      </div>
    </FilterChip>
  )
}

export default function CompanyDirectory({ category, title, subtitle }) {
  // THE REP'S SENT FOLDER, loaded once. It is the second half of "have we
  // contacted them?" — HubSpot holds only what somebody logged to it. Null
  // until it lands (and if it never does), which reads as "HubSpot only",
  // never as "nobody was emailed".
  const [sentMail, setSentMail] = useState(null)
  useEffect(() => {
    let alive = true
    loadSentMail().then((idx) => {
      if (alive) setSentMail(idx)
    })
    return () => {
      alive = false
    }
  }, [])
  // PRODUCT USAGE from the portal's custom contact properties, one call for
  // the whole table (GET /api/hubspot/users). The usage columns join each row
  // to `by_company` through the match the data layer already made
  // (row._hubspot.companyId — lib/leadUsage.js). Null until the read lands; a
  // failure resolves {available:false, reason} and the columns keep rendering
  // dashes while the badge next to Sync admits the data could not be read —
  // unread is not empty, and this page never claims otherwise.
  const [usage, setUsage] = useState(null)
  useEffect(() => {
    let alive = true
    const ctrl = new AbortController()
    hubspotUsers({ signal: ctrl.signal }).then((res) => {
      if (alive) setUsage(res)
    })
    return () => {
      alive = false
      ctrl.abort()
    }
  }, [])
  const usageNote = usageStatusNote(usage)
  // Rows come from the encrypted IndexedDB mirror (synced from the leads API),
  // so the full database is available locally and filters run over everything.
  const { rows: importedRows, loading, meta, syncing, progress, sync } = useLeadsCache(category)
  const { mode, manifest, refresh: refreshManifest } = useManifest()
  const { can } = useRbac()
  const navigate = useNavigate()

  // Manually-entered leads merge in ahead of the import; each carries
  // _source/_addedBy for the Source column + filter.
  const rows = useMemo(
    () => [...listManualLeads(category), ...importedRows],
    [importedRows, category],
  )

  const [searchParams, setSearchParams] = useSearchParams()
  const requestedRegion = searchParams.get('region')

  // FILTERS LIVE IN THE URL (lib/directoryFilters.js): every filter below
  // starts from the query string and is written back to it, so Back from a
  // lead lands on the same filtered list (user request, 2026-10-20). The
  // ?state=XX deep link the Market Map and the import wizard send is the same
  // `state` param, so it needs no effect of its own any more.
  const fromUrl = useRef(null)
  if (!fromUrl.current) fromUrl.current = filtersFromParams(searchParams)
  const [geo, setGeo] = useState(() => fromUrl.current.geo)
  const [query, setQuery] = useState(() => fromUrl.current.query)
  const [bands, setBands] = useState(() => ({ ...EMPTY_BANDS, ...fromUrl.current.bands }))
  const [seeded, setSeeded] = useState(false)
  // Default view: only leads that are actually workable — a primary contact or
  // an email on file. Toggleable so the full directory is one click away.
  const [reachableOnly, setReachableOnly] = useState(() => fromUrl.current.reachableOnly)
  // Trade + source facets. Source values: 'imported' | 'manual' | 'by:<email>'.
  const [tradesSel, setTradesSel] = useState(() => fromUrl.current.trades)
  const [sources, setSources] = useState(() => fromUrl.current.sources)
  // Outreach state, from the HubSpot mirror: replied / reached out / not
  // contacted. Empty = every row, so the directory opens unfiltered.
  const [outreach, setOutreach] = useState(() => fromUrl.current.outreach)
  // Quality flags + a minimum intent score — the cuts that were only reachable
  // from a saved segment before, where nothing downstream could use them.
  const [flags, setFlags] = useState(() => ({ ...EMPTY_FLAGS, ...fromUrl.current.flags }))
  const [minScore, setMinScore] = useState(() => fromUrl.current.minScore)
  // One calibration read per mount, shared by the score filter and the table.
  const calib = useMemo(() => calibration(), [])
  // Saved views: this exact filter state, named, re-applied against live rows.
  const [viewsTick, setViewsTick] = useState(0)
  const views = useMemo(() => listViews(category), [category, viewsTick])

  // Selection + save-to-Intelligence live here so the actions can sit next to
  // Sync in the filter row, and "N selected" can live in the table footer.
  const [selected, setSelected] = useState(() => new Set())
  // Enriched marks persist per account (lib/enrichedLeads.js); subscribe so
  // rows badge up live while an enrichment batch is running.
  const [enriched, setEnrichedState] = useState(() => enrichedSet())
  // Rows in an active batch badge "Enriching…"; the same subscription drives
  // both, so a row flips to "Enriched" the moment its job reports.
  const [enriching, setEnrichingState] = useState(() => enrichingSet())
  useEffect(
    () =>
      subscribeEnriched(() => {
        setEnrichedState(enrichedSet())
        setEnrichingState(enrichingSet())
      }),
    [],
  )
  // An enrichment batch that settles has written its findings back onto any
  // IMPORTED rows it researched (custom-leads table) and stamped the region.
  // Re-read the manifest so the mirror's fingerprint changes and the table
  // re-pulls those regions - the score, contacts and owners appear without a
  // manual Sync. Twice: once soon after, and once more for a straggler whose
  // write-back landed after its job reported done. Loaded regions are never
  // written back, so a category with no imported region skips this.
  const hasImported = (manifest?.categories?.[category] || []).some((r) => r.imported)
  const wasEnriching = useRef(false)
  useEffect(() => {
    const busy = enriching.size > 0
    if (wasEnriching.current && !busy && hasImported) {
      const soon = setTimeout(() => refreshManifest?.().catch(() => null), 4000)
      const later = setTimeout(() => refreshManifest?.().catch(() => null), 45000)
      wasEnriching.current = busy
      return () => {
        clearTimeout(soon)
        clearTimeout(later)
      }
    }
    wasEnriching.current = busy
    return undefined
  }, [enriching, hasImported, refreshManifest])
  const [columnsOpen, setColumnsOpen] = useState(false) // leads-table column picker
  const [flash, setFlash] = useState(null)
  const timerRef = useRef(null)

  useEffect(() => () => clearTimeout(timerRef.current), [])

  // ---- Responsive filtering ----
  // The chips render from the state above (so a click paints its checked state
  // on the very next frame), while the 20k+ row scan reads DEFERRED copies —
  // React renders the heavy table in a later, interruptible pass. Without this
  // the filter predicate work blocks the paint and the checkbox feels stuck.
  const dQuery = useDeferredValue(query)
  const dGeo = useDeferredValue(geo)
  const dBands = useDeferredValue(bands)
  const dFlags = useDeferredValue(flags)
  const dMinScore = useDeferredValue(minScore)
  const dTrades = useDeferredValue(tradesSel)
  const dSources = useDeferredValue(sources)
  const dOutreach = useDeferredValue(outreach)
  const dReachableOnly = useDeferredValue(reachableOnly)
  // True while the table still reflects the previous filters.
  const filtersPending =
    dQuery !== query ||
    dGeo !== geo ||
    dBands !== bands ||
    dFlags !== flags ||
    dMinScore !== minScore ||
    dTrades !== tradesSel ||
    dSources !== sources ||
    dOutreach !== outreach ||
    dReachableOnly !== reachableOnly

  // ---- Faceted counts ----
  // One predicate per filter, so each facet's option counts can be computed
  // over the rows passing every OTHER filter (its own selections excluded —
  // otherwise checking Texas would zero out Florida).
  const passes = useMemo(() => {
    const q = dQuery.trim().toLowerCase()
    const geoPred = geoPredicate(dGeo)
    return {
      reachable: (c) =>
        !dReachableOnly || (c.email || '').includes('@') || (c.contact_name || '').trim(),
      geo: geoPred,
      // DEEP search: the whole record — every column plus every contact in
      // the packed blob (lib/leadSearch.js) — so a contact's name, a second
      // email, an owner or estimator, a website, all find the company.
      search: (c) => leadMatches(c, q),
      // Outreach: the mirror's own answer per row. A lead with no matched CRM
      // record ('unknown') matches NO selection — it cannot be claimed as
      // contacted or as untouched, so a filter must not sweep it into either.
      outreach: (c) => !dOutreach.length || dOutreach.includes(outreachOf(c, sentMail).state),
      trade: (c) => {
        if (!dTrades.length) return true
        const ts = splitTrades(c.trade)
        return ts.some((t) => dTrades.includes(t))
      },
      source: (c) => {
        if (!dSources.length) return true
        const isManual = c._source === 'manual'
        return dSources.some((s) =>
          s === 'imported'
            ? !isManual
            : s === 'manual'
              ? isManual
              : isManual && c._addedBy === s.slice(3),
        )
      },
      // Quality flags, AND-ed: each is off by default so the directory opens
      // unfiltered. `activeProjects` reads the packed projects blob.
      flags: (c) =>
        (!dFlags.certified || certified(c)) &&
        (!dFlags.volumeDisclosed || volumeDisclosed(c)) &&
        (!dFlags.activeProjects || projectMeta(c.projects).active > 0),
      // Minimum intent score — the same number the Intent column sorts by.
      score: (c) => dMinScore <= 0 || intentScoreOf(c, calib) >= dMinScore,
      band: {
        size: (c) => !dBands.size.length || dBands.size.includes(sizeBandOf(c)),
        enriched: (c) =>
          !dBands.enriched.length ||
          dBands.enriched.includes(isRowEnriched(c, enriched) ? 'yes' : 'no'),
        volume: (c) => !dBands.volume.length || dBands.volume.includes(volumeBandOf(c)),
        website: (c) => !dBands.website.length || dBands.website.includes(websiteBandOf(c)),
      },
    }
  }, [dGeo, dQuery, dReachableOnly, dBands, dTrades, dSources, dOutreach, enriched, dFlags, dMinScore, calib, sentMail])

  // State/city counts in the geo picker reflect every filter except geo itself.
  const geoIndex = useMemo(
    () =>
      buildGeoIndex(
        rows.filter(
          (c) =>
            passes.reachable(c) &&
            passes.search(c) &&
            passes.trade(c) &&
            passes.source(c) &&
            passes.outreach(c) &&
            passes.band.size(c) &&
            passes.band.volume(c) &&
            passes.band.website(c) &&
            passes.band.enriched(c) &&
            passes.flags(c) &&
            passes.score(c),
        ),
      ),
    [rows, passes],
  )

  // Deep link from global search (?region=<slug>): once rows arrive, preselect
  // the states that region covers, in state mode. The URL sync below then
  // writes those as state= and drops region=.
  useEffect(() => {
    if (seeded || !requestedRegion || !rows.length) return
    const states = new Set()
    for (const c of rows) {
      if (c._region !== requestedRegion) continue
      const p = parseCityState(c.city_state)
      if (p) states.add(p.state)
    }
    if (states.size) setGeo({ mode: 'state', states: [...states], cities: [] })
    setSeeded(true)
  }, [requestedRegion, rows, seeded])

  // Filters -> URL. REPLACE, not push: tweaking a chip must not add a history
  // entry per click, or Back would walk through every filter change before it
  // left the page. The ref holds the last string this page wrote, so the
  // URL -> filters effect below can tell its own writes from a navigation.
  const wroteUrl = useRef(searchParams.toString())
  useEffect(() => {
    // A ?region= link is still waiting for rows to resolve it into states.
    if (requestedRegion && !seeded) return
    const next = filtersToParams(
      { geo, query, bands, flags, minScore, reachableOnly, trades: tradesSel, sources, outreach },
      { keep: searchParams, drop: ['region'] },
    )
    if (next === searchParams.toString()) return
    wroteUrl.current = next
    setSearchParams(next, { replace: true })
    // searchParams is read, not reacted to: the effect runs when a filter does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo, query, bands, flags, minScore, reachableOnly, tradesSel, sources, outreach, requestedRegion, seeded])

  // URL -> filters, for a navigation that lands here while the page is already
  // open (the sidebar's plain "Masonry", a Market Map ?state= link, Back/Forward
  // between two filtered URLs). The page's own writes are skipped.
  useEffect(() => {
    const current = searchParams.toString()
    if (current === wroteUrl.current) return
    wroteUrl.current = current
    if (searchParams.get('region')) {
      setSeeded(false) // a new ?region= link: let the region effect resolve it again
      return
    }
    const f = filtersFromParams(searchParams)
    setGeo(f.geo)
    setQuery(f.query)
    setBands({ ...EMPTY_BANDS, ...f.bands })
    setFlags({ ...EMPTY_FLAGS, ...f.flags })
    setMinScore(f.minScore)
    setReachableOnly(f.reachableOnly)
    setTradesSel(f.trades)
    setSources(f.sources)
    setOutreach(f.outreach)
  }, [searchParams])

  // The cheap base the visible set is built on — the three filters that cut the
  // most rows, computed once and reused below.
  const prefiltered = useMemo(
    () => rows.filter((c) => passes.reachable(c) && passes.geo(c) && passes.search(c)),
    [rows, passes],
  )

  // Each band chip's counts: rows passing everything except that chip's own
  // group, so picking a Size narrows the Volume/Website numbers live.
  const bandCounts = useMemo(() => {
    const counts = { size: {}, volume: {}, website: {}, enriched: {} }
    for (const c of rows) {
      if (
        !(
          passes.reachable(c) &&
          passes.geo(c) &&
          passes.search(c) &&
          passes.trade(c) &&
          passes.source(c) &&
          passes.outreach(c) &&
          passes.flags(c) &&
          passes.score(c)
        )
      )
        continue
      const ps = passes.band.size(c)
      const pv = passes.band.volume(c)
      const pw = passes.band.website(c)
      const pe = passes.band.enriched(c)
      if (pv && pw && pe) {
        const s = sizeBandOf(c)
        if (s) counts.size[s] = (counts.size[s] || 0) + 1
      }
      if (ps && pw && pe) {
        const v = volumeBandOf(c)
        if (v) counts.volume[v] = (counts.volume[v] || 0) + 1
      }
      if (ps && pv && pe) {
        const w = websiteBandOf(c)
        counts.website[w] = (counts.website[w] || 0) + 1
      }
      if (ps && pv && pw) {
        const e = isRowEnriched(c, enriched) ? 'yes' : 'no'
        counts.enriched[e] = (counts.enriched[e] || 0) + 1
      }
    }
    return counts
  }, [rows, passes, enriched])

  // The table itself: every filter applied (facet counts above intentionally
  // drop one filter each; the visible rows drop none).
  const filtered = useMemo(
    () =>
      prefiltered.filter(
        (c) =>
          passes.trade(c) &&
          passes.source(c) &&
          passes.outreach(c) &&
          passes.band.size(c) &&
          passes.band.volume(c) &&
          passes.band.website(c) &&
          passes.band.enriched(c) &&
          passes.flags(c) &&
          passes.score(c),
      ),
    [prefiltered, passes],
  )

  // Trade facet: distinct trades + counts over rows passing every filter except
  // trade itself (so multi-selecting trades keeps honest OR counts).
  const tradeCounts = useMemo(() => {
    const counts = new Map()
    for (const c of rows) {
      if (
        !(
          passes.reachable(c) &&
          passes.geo(c) &&
          passes.search(c) &&
          passes.source(c) &&
          passes.outreach(c) &&
          passes.band.size(c) &&
          passes.band.volume(c) &&
          passes.band.website(c) &&
          passes.band.enriched(c) &&
          passes.flags(c) &&
          passes.score(c)
        )
      )
        continue
      for (const t of splitTrades(c.trade)) counts.set(t, (counts.get(t) || 0) + 1)
    }
    return [...counts.entries()]
      .map(([trade, count]) => ({ trade, count }))
      .sort((a, b) => b.count - a.count)
  }, [rows, passes])

  // Outreach facet counts, over rows passing every filter EXCEPT outreach —
  // same rule as every other facet, so the numbers track the current view.
  const outreachCounts = useMemo(() => {
    const counts = { replied: 0, sent: 0, none: 0, unknown: 0 }
    for (const c of rows) {
      if (
        !(
          passes.reachable(c) &&
          passes.geo(c) &&
          passes.search(c) &&
          passes.trade(c) &&
          passes.source(c) &&
          passes.band.size(c) &&
          passes.band.volume(c) &&
          passes.band.website(c) &&
          passes.band.enriched(c) &&
          passes.flags(c) &&
          passes.score(c)
        )
      )
        continue
      counts[outreachOf(c, sentMail).state] += 1
    }
    return counts
  }, [rows, passes])

  // Source facet: imported vs manual (and per-collaborator) over rows passing
  // every filter except source itself.
  const sourceCounts = useMemo(() => {
    let imported = 0
    let manual = 0
    const byUser = new Map()
    for (const c of rows) {
      if (
        !(
          passes.reachable(c) &&
          passes.geo(c) &&
          passes.search(c) &&
          passes.trade(c) &&
          passes.band.size(c) &&
          passes.band.volume(c) &&
          passes.band.website(c) &&
          passes.band.enriched(c) &&
          passes.flags(c) &&
          passes.outreach(c) &&
          passes.score(c)
        )
      )
        continue
      if (c._source === 'manual') {
        manual += 1
        const k = c._addedBy || 'unknown'
        byUser.set(k, (byUser.get(k) || 0) + 1)
      } else {
        imported += 1
      }
    }
    return { imported, manual, byUser }
  }, [rows, passes])
  const contributors = useMemo(() => manualContributors(category), [category, rows])

  const toggleBand = (group, value) =>
    setBands((prev) => ({
      ...prev,
      [group]: prev[group].includes(value)
        ? prev[group].filter((v) => v !== value)
        : [...prev[group], value],
    }))
  const clearBand = (group) => setBands((prev) => ({ ...prev, [group]: [] }))
  const toggleFlag = (key) => setFlags((prev) => ({ ...prev, [key]: !prev[key] }))
  const flagCount = Object.values(flags).filter(Boolean).length + (minScore > 0 ? 1 : 0)

  // A view is exactly the filter state — restoring one is a set of setState
  // calls, so a saved view can never drift from what the page can express.
  function applyView(v) {
    const st = v?.state || {}
    setGeo(st.geo || EMPTY_GEO)
    setQuery(st.query || '')
    setBands({ ...EMPTY_BANDS, ...(st.bands || {}) })
    setFlags({ ...EMPTY_FLAGS, ...(st.flags || {}) })
    setMinScore(st.minScore || 0)
    setReachableOnly(st.reachableOnly !== false)
    setTradesSel(st.trades || [])
    setSources(st.sources || [])
    showFlash(`Applied “${v.name}”`)
  }

  function handleSaveView() {
    const name = window.prompt('Name this view')
    if (!name) return
    saveView(category, name, {
      geo,
      query,
      bands,
      flags,
      minScore,
      reachableOnly,
      trades: tradesSel,
      sources,
    })
    setViewsTick((n) => n + 1)
    showFlash(`Saved “${name.trim()}”`)
  }

  const geoKey = geoCount(geo) ? `${geo.mode}:${geo.states.join(',')}:${geo.cities.join(',')}` : 'all'
  const bandsKey = `${bands.size.join(',')}|${bands.volume.join(',')}|${bands.website.join(',')}|${bands.enriched.join(',')}|${flags.certified}${flags.activeProjects}${flags.volumeDisclosed}|${minScore}`
  const facetKey = `${tradesSel.join(',')}|${sources.join(',')}`

  // Clear selection when the visible set changes (filter/search).
  useEffect(() => {
    setSelected(new Set())
  }, [geoKey, query, bandsKey, facetKey])

  const toggleRow = (k) =>
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(k) ? next.delete(k) : next.add(k)
      return next
    })
  const toggleAll = (pageKeys) =>
    setSelected((prev) => {
      const next = new Set(prev)
      const all = pageKeys.every((k) => next.has(k))
      pageKeys.forEach((k) => (all ? next.delete(k) : next.add(k)))
      return next
    })

  function showFlash(msg) {
    setFlash(msg)
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => setFlash(null), 2200)
  }

  // Exactly one selected lead with an email: write it a real email, which sends
  // through Apollo (Gmail is read-only, for replies). Writing to a multi-select
  // is not something this app does.
  const soleSelected =
    selected.size === 1 ? filtered.find((c) => selected.has(keyOf(c))) || null : null

  function handleSendEmail() {
    if (!soleSelected?.email) return
    openCompose({
      to: soleSelected.email,
      company: soleSelected.name,
      city: soleSelected.city_state || '',
      trade: soleSelected.trade || '',
      contactName: soleSelected.contact_name || '',
    })
  }

  // Runs the real AI enrichment pipeline (OpenAI web research + Apollo contacts
  // + a 0-10 score) on the selected rows, one-by-one, capped at 10 per run.
  // Opens a panel that streams per-row progress.
  function handleRunEnrich() {
    if (!can('leads.enrich')) return
    // If a batch is already running (e.g. after "Run in background"), re-attach
    // to it instead of starting — and paying for — a fresh one.
    const active = loadActiveBatch()
    if (active && active.category === category && !enrichmentRunning()) {
      resumeEnrichment(active.batchId)
      return
    }
    const picked = filtered.filter((c) => selected.has(keyOf(c)))
    if (!picked.length) return
    // Each run costs real OpenAI/Apollo spend, so a row is only re-researched
    // once its dossier is a day old (lib/enrichment.js owns that rule). Rows
    // enriched more recently are skipped, and we say how many.
    const fresh = picked.filter((c) => reenrichState(keyOf(c)).allowed)
    const skipped = picked.length - fresh.length
    if (!fresh.length) {
      showFlash(
        picked.length === 1
          ? 'Enriched less than a day ago — open the lead to re-run it'
          : `All ${picked.length} were enriched today — nothing to re-run yet`,
      )
      return
    }
    if (skipped > 0) showFlash(`Skipping ${skipped} enriched in the last 24h`)
    runEnrichment(fresh, category)
    setSelected(new Set())
  }

  // "Enrich next 10": nothing is pre-enriched, so the directory researches as
  // the team works through it - the next ten leads in the current view that
  // nobody has researched yet, on the spot, then the next ten. Same pipeline,
  // same spend gates as Enrich on a selection: not already enriched, not in
  // flight, not inside the 24h re-run window, not already paid for (the
  // research ledger). CRM-only rows have no directory row to research.
  const nextToEnrich = useMemo(() => {
    const out = []
    for (const c of filtered) {
      if (out.length >= MAX_ENRICH_ROWS) break
      if (c._crmOnly) continue
      const k = keyOf(c)
      if (!k || enriching.has(k) || isRowEnriched(c, enriched) || isResearched(k)) continue
      if (!reenrichState(k).allowed) continue
      out.push(c)
    }
    return out
  }, [filtered, enriched, enriching])

  function handleEnrichNext() {
    if (!can('leads.enrich')) return
    const active = loadActiveBatch()
    if (active && active.category === category && !enrichmentRunning()) {
      resumeEnrichment(active.batchId)
      return
    }
    if (!nextToEnrich.length) {
      showFlash('Every lead in this view is enriched')
      return
    }
    runEnrichment(nextToEnrich, category)
  }

  const firstSync = loading || (syncing && rows.length === 0)
  const hasSelection = selected.size > 0

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageHeader
        title={title}
        subtitle={subtitle}
        actions={
          <div className="flex items-center gap-x-2">
            {can('leads.import') && (
              <button
                type="button"
                className="inline-flex items-center gap-x-1.5 rounded-[9px] border border-line bg-surface px-[14px] py-[7px] text-[13px] font-semibold text-ink transition-colors hover:bg-panel"
                onClick={() => navigate(`/leads/import?category=${category}`)}
                title="Import a CSV or .xlsx of leads - match the columns, check every row, then import. Nothing is enriched until you press Enrich."
              >
                <DownloadIcon className="h-4 w-4" />
                Import
              </button>
            )}
            <button
              type="button"
              className="inline-flex items-center gap-x-1.5 rounded-[9px] bg-primary px-[14px] py-[7px] text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark"
              onClick={() => navigate(`/leads/new?category=${category}`)}
              title="Add a lead by hand — it merges into this directory with you as the source"
            >
              <PlusIcon className="h-4 w-4" />
              Add lead
            </button>
          </div>
        }
      />

      {/* Row 1 — search · sync · columns · selection actions */}
      <section className="mb-[12px] flex flex-wrap items-center gap-x-[14px] gap-y-[10px]">
        <input
          className="w-[320px] max-w-full rounded-[9px] border border-line bg-surface px-[14px] py-[10px] text-[14px] text-ink focus:outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgba(60,135,247,0.2)]"
          type="search"
          placeholder="Search company, city, industry, contact…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="ml-auto flex flex-none items-center gap-x-3">
          {/* The API fallback is easy to miss — make it loud. Sample mode means
              the dev server is running without API_PROXY (or the API is down),
              so counts reflect the small bundled CSVs, not the real database. */}
          {mode === 'static' && (
            <span
              className="whitespace-nowrap rounded-full bg-[#fefce8] px-3 py-[6px] text-[12px] font-semibold text-[#a16207] dark:bg-[rgba(161,98,7,0.18)] dark:text-[#fbbf24]"
              title="The leads API isn't reachable — showing the small bundled sample. Start the dev server with API_PROXY set (launch config “start”) to load the full database."
            >
              ⚠ Sample data — API not connected
            </span>
          )}
          {/* The usage columns' status, in the same spot the sample-data
              warning lives. Only speaks when there is something to admit
              (could not read / not connected / incomplete) — a clean, complete
              read needs no badge. The full sentence rides in the tooltip. */}
          {usageNote && (
            <span
              className={`whitespace-nowrap rounded-full px-3 py-[6px] text-[12px] font-semibold ${
                usageNote.tone === 'warning'
                  ? 'bg-[#fefce8] text-[#a16207] dark:bg-[rgba(161,98,7,0.18)] dark:text-[#fbbf24]'
                  : 'bg-panel text-dim'
              }`}
              title={usageNote.detail}
            >
              {usageNote.tone === 'warning' ? '⚠ ' : ''}
              {usageNote.label}
            </span>
          )}
          {/* Manual sync is a force refresh: server truth overwrites the mirror. */}
          <SyncButton meta={meta} syncing={syncing} progress={progress} onSync={() => sync(true)} />
          <button
            type="button"
            className="inline-flex cursor-pointer items-center gap-x-[7px] rounded-[9px] border border-line bg-surface px-3 py-2 text-[13px] font-semibold text-ink transition-colors hover:bg-panel"
            onClick={() => setColumnsOpen(true)}
            title="Choose which columns to show and rows per page"
          >
            <svg viewBox="0 0 24 24" fill="none" className="h-[15px] w-[15px] text-dim" aria-hidden="true">
              {/* coolicons: line/Columns.svg */}
              <path d="M14 6.5V17.5C14 17.9647 14 18.197 14.0384 18.3902C14.1962 19.1836 14.8165 19.8036 15.6099 19.9614C15.8031 19.9999 16.0354 19.9999 16.5 19.9999C16.9647 19.9999 17.1969 19.9999 17.3901 19.9614C18.1835 19.8036 18.8036 19.1836 18.9614 18.3902C18.9999 18.197 19 17.9647 19 17.5V6.5C19 6.03535 18.9999 5.80306 18.9614 5.60986C18.8036 4.81648 18.1836 4.19624 17.3902 4.03843C17.197 4 16.9647 4 16.5 4C16.0353 4 15.8031 4 15.6099 4.03843C14.8165 4.19624 14.1962 4.81648 14.0384 5.60986C14 5.80306 14 6.03534 14 6.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M5 6.5V17.5C5 17.9647 5 18.197 5.03843 18.3902C5.19624 19.1836 5.81648 19.8036 6.60986 19.9614C6.80306 19.9999 7.03539 19.9999 7.50004 19.9999C7.96469 19.9999 8.19694 19.9999 8.39014 19.9614C9.18352 19.8036 9.80361 19.1836 9.96143 18.3902C9.99986 18.197 10 17.9647 10 17.5V6.5C10 6.03535 9.99986 5.80306 9.96143 5.60986C9.80361 4.81648 9.18356 4.19624 8.39018 4.03843C8.19698 4 7.96466 4 7.5 4C7.03534 4 6.80306 4 6.60986 4.03843C5.81648 4.19624 5.19624 4.81648 5.03843 5.60986C5 5.80306 5 6.03534 5 6.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Columns
          </button>
          {flash && (
            <span className="whitespace-nowrap rounded-[8px] bg-[#f0fdf4] px-3 py-2 text-[13px] font-semibold text-[#15803d] dark:bg-[rgba(34,197,94,0.15)] dark:text-[#4ade80]">
              ✓ {flash}
            </span>
          )}
          {!hasSelection && !firstSync && (
            <button
              type="button"
              className="inline-flex items-center gap-x-1.5 rounded-[9px] border border-transparent bg-primary px-[14px] py-2 text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
              onClick={handleEnrichNext}
              disabled={!can('leads.enrich') || (!nextToEnrich.length && !loadActiveBatch())}
              title={
                !can('leads.enrich')
                  ? "Your role can't run enrichment"
                  : nextToEnrich.length
                    ? `Research the next ${nextToEnrich.length} un-enriched lead${nextToEnrich.length === 1 ? '' : 's'} in this view on the spot: AI web research, Apollo contacts and a 0–10 score. ${nextToEnrich
                        .slice(0, 3)
                        .map((c) => c.name)
                        .join(', ')}${nextToEnrich.length > 3 ? '…' : ''}`
                    : 'Every lead in this view is enriched or already being researched'
              }
            >
              <SparklesIcon className="h-4 w-4" />
              {nextToEnrich.length ? `Enrich next ${nextToEnrich.length}` : 'All enriched'}
            </button>
          )}
          {hasSelection && (
            <>
              <button
                type="button"
                className="inline-flex items-center gap-x-1.5 rounded-[9px] border border-transparent bg-primary px-[14px] py-2 text-[13px] font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleRunEnrich}
                disabled={!can('leads.enrich')}
                title={!can('leads.enrich') ? "Your role can't run enrichment" : `Enrich up to ${MAX_ENRICH_ROWS} selected companies with AI web research + verified contacts (Apollo) + a 0–10 lead score${
                  selected.size > MAX_ENRICH_ROWS ? ` — first ${MAX_ENRICH_ROWS} of ${selected.size}` : ''
                }`}
              >
                <SparklesIcon className="h-4 w-4" />
                Enrich{selected.size > MAX_ENRICH_ROWS ? ` (${MAX_ENRICH_ROWS})` : ''}
              </button>
              {soleSelected?.email && (
                <button
                  type="button"
                  className="inline-flex items-center gap-x-1.5 rounded-[9px] border border-line bg-surface px-[14px] py-2 text-[13px] font-semibold text-dim transition-colors hover:bg-panel hover:text-ink"
                  onClick={handleSendEmail}
                  title={`Write to ${soleSelected.email} — sends through Apollo`}
                >
                  <MailIcon className="h-4 w-4" />
                  Send email
                </button>
              )}
            </>
          )}
        </div>
      </section>

      {/* Row 2 — filters */}
      <section className="mb-[14px] flex flex-wrap items-center gap-x-[14px] gap-y-[10px]">
        <GeoPicker index={geoIndex} value={geo} onChange={setGeo} disabled={firstSync} />
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-2">
          <button
            type="button"
            className={`inline-flex items-center gap-x-1.5 rounded-[10px] border px-3 py-2 text-[13px] font-semibold transition-colors ${
              reachableOnly
                ? 'border-primary bg-primary-tint text-primary-dark'
                : 'border-line bg-surface text-dim hover:bg-panel hover:text-ink'
            }`}
            onClick={() => setReachableOnly((v) => !v)}
            title={
              reachableOnly
                ? 'Showing only leads with a primary contact or email (default) — click to show every company'
                : 'Showing every company — click to show only leads with a primary contact or email'
            }
          >
            {/* Only the check, and only when on. The empty circle it used to
                draw when off was visual noise — the chip's own active styling
                already says whether the filter is applied. */}
            {reachableOnly && (
              <svg viewBox="0 0 24 24" fill="none" className="h-[14px] w-[14px]" aria-hidden="true">
                {/* coolicons: line/Check.svg */}
                <path d="M6 12L10.2426 16.2426L18.727 7.75732" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
            Has contact
          </button>
          <TradeChip
            options={tradeCounts}
            values={tradesSel}
            onToggle={(t) =>
              setTradesSel((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]))
            }
            onClear={() => setTradesSel([])}
          />
          <BandChip
            label="Size"
            options={SIZE_BANDS}
            values={bands.size}
            counts={bandCounts.size}
            onToggle={(v) => toggleBand('size', v)}
            onClear={() => clearBand('size')}
          />
          <BandChip
            label="Annual Volume"
            options={VOLUME_BANDS}
            values={bands.volume}
            counts={bandCounts.volume}
            onToggle={(v) => toggleBand('volume', v)}
            onClear={() => clearBand('volume')}
          />
          <BandChip
            label="Website"
            options={WEBSITE_BANDS}
            values={bands.website}
            counts={bandCounts.website}
            onToggle={(v) => toggleBand('website', v)}
            onClear={() => clearBand('website')}
          />
          <BandChip
            label="Enriched"
            options={ENRICHED_BANDS}
            values={bands.enriched}
            counts={bandCounts.enriched}
            onToggle={(v) => toggleBand('enriched', v)}
            onClear={() => clearBand('enriched')}
          />
          {/* Quality — the cuts that used to be reachable only from a saved
              segment, where nothing could act on the result. */}
          <FilterChip
            label="Quality"
            count={flagCount}
            active={flagCount > 0}
            onClear={() => {
              setFlags(EMPTY_FLAGS)
              setMinScore(0)
            }}
          >
            <CheckRow checked={flags.certified} onToggle={() => toggleFlag('certified')}>
              Has certifications
            </CheckRow>
            <CheckRow checked={flags.activeProjects} onToggle={() => toggleFlag('activeProjects')}>
              Has active projects
            </CheckRow>
            <CheckRow checked={flags.volumeDisclosed} onToggle={() => toggleFlag('volumeDisclosed')}>
              Discloses annual volume
            </CheckRow>
            <div className="mt-1 [border-top:1px_solid_var(--table-line)] px-2.5 pb-1 pt-2">
              <div className="mb-1.5 flex items-center justify-between text-[12px] text-dim">
                <span>Min intent score</span>
                <span className="font-semibold tabular-nums text-ink">{minScore || 'any'}</span>
              </div>
              <input
                type="range"
                min={0}
                max={90}
                step={5}
                value={minScore}
                onChange={(e) => setMinScore(Number(e.target.value))}
                onClick={(e) => e.stopPropagation()}
                className="w-full cursor-pointer accent-[color:var(--primary)]"
              />
            </div>
          </FilterChip>
          {/* OUTREACH — who we have already talked to, and who answered. Read
              from the HubSpot mirror per company (lib/companyMeta.js#outreachOf),
              which is the only witness: this app has never sent an email. The
              options are deliberately three, not two: a lead with no matched CRM
              record is in none of them, because "we cannot see" is not "never
              contacted". */}
          <FilterChip
            label="Outreach"
            count={outreach.length}
            active={outreach.length > 0}
            onClear={() => setOutreach([])}
          >
            <CheckRow
              checked={outreach.includes('replied')}
              onToggle={() =>
                setOutreach((p) => (p.includes('replied') ? p.filter((x) => x !== 'replied') : [...p, 'replied']))
              }
              sub={outreachCounts.replied.toLocaleString()}
            >
              Replied to us
            </CheckRow>
            <CheckRow
              checked={outreach.includes('sent')}
              onToggle={() =>
                setOutreach((p) => (p.includes('sent') ? p.filter((x) => x !== 'sent') : [...p, 'sent']))
              }
              sub={outreachCounts.sent.toLocaleString()}
            >
              Reached out, no reply
            </CheckRow>
            <CheckRow
              checked={outreach.includes('none')}
              onToggle={() =>
                setOutreach((p) => (p.includes('none') ? p.filter((x) => x !== 'none') : [...p, 'none']))
              }
              sub={outreachCounts.none.toLocaleString()}
            >
              Never contacted
            </CheckRow>
            <p className="m-0 px-[10px] pb-1 pt-2 text-[11.5px] leading-[1.45] text-faint">
              {outreachCounts.unknown.toLocaleString()} leads have no matched HubSpot record, so
              none of these can be said of them. They are in no option above.
            </p>
          </FilterChip>
          {(sourceCounts.manual > 0 || sources.length > 0) && (
            <FilterChip
              label="Source"
              count={sources.length}
              active={sources.length > 0}
              onClear={() => setSources([])}
            >
              <CheckRow
                checked={sources.includes('imported')}
                onToggle={() =>
                  setSources((p) => (p.includes('imported') ? p.filter((x) => x !== 'imported') : [...p, 'imported']))
                }
                sub={sourceCounts.imported.toLocaleString()}
              >
                Imported database
              </CheckRow>
              <CheckRow
                checked={sources.includes('manual')}
                onToggle={() =>
                  setSources((p) => (p.includes('manual') ? p.filter((x) => x !== 'manual') : [...p, 'manual']))
                }
                sub={sourceCounts.manual.toLocaleString()}
              >
                Added manually
              </CheckRow>
              {contributors.length > 0 && (
                <div className="mt-1 border-t border-line pt-1">
                  <div className="px-2.5 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-[0.04em] text-faint">
                    By teammate
                  </div>
                  {contributors.map((p) => {
                    const val = `by:${p.email}`
                    return (
                      <CheckRow
                        key={p.email}
                        checked={sources.includes(val)}
                        onToggle={() =>
                          setSources((prev) =>
                            prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val],
                          )
                        }
                        sub={(sourceCounts.byUser.get(p.email) || 0).toLocaleString()}
                      >
                        {p.name}
                      </CheckRow>
                    )
                  })}
                </div>
              )}
            </FilterChip>
          )}
          {/* Saved views — a named filter state, re-applied against live rows.
              This is what a "segment" was, and it lives where the filters are:
              a view is read back through them, never frozen into a copy. */}
          <FilterChip
            label="Views"
            count={0}
            active={false}
            onClear={undefined}
          >
            <button
              type="button"
              onClick={handleSaveView}
              className="flex w-full cursor-pointer items-center gap-x-2 rounded-[8px] border-none bg-transparent px-2.5 py-2 text-left text-[13px] font-semibold text-primary-dark transition-colors hover:bg-panel"
            >
              <PlusIcon className="h-[14px] w-[14px]" />
              Save this view
            </button>
            {views.length > 0 && (
              <div className="mt-1 [border-top:1px_solid_var(--table-line)] pt-1">
                {views.map((v) => (
                  <div key={v.id} className="group flex items-center gap-x-1">
                    <button
                      type="button"
                      onClick={() => applyView(v)}
                      className="min-w-0 flex-1 cursor-pointer truncate rounded-[8px] border-none bg-transparent px-2.5 py-2 text-left text-[13px] text-ink transition-colors hover:bg-panel"
                    >
                      {v.name}
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${v.name}`}
                      onClick={() => {
                        deleteView(v.id)
                        setViewsTick((n) => n + 1)
                      }}
                      className="mr-1 flex-none cursor-pointer rounded-[6px] border-none bg-transparent px-1.5 py-1 text-[13px] text-faint opacity-0 transition-opacity hover:text-danger group-hover:opacity-100"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
            {views.length === 0 && (
              <div className="px-2.5 pb-1 pt-0.5 text-[12px] text-faint">
                No saved views yet — filter the table, then save it.
              </div>
            )}
          </FilterChip>
        </div>
      </section>

      <div className="min-h-0 flex-1">
        {/* While the first sync runs the table keeps its full chrome and shows a
            centered spinner, so nothing jumps when the rows land. */}
        <div
          className={`h-full transition-opacity duration-150 ${filtersPending ? 'opacity-60' : 'opacity-100'}`}
        >
        <CompanyTable
          companies={firstSync ? [] : filtered}
          category={category}
          sentMail={sentMail}
          usage={usage}
          selectedKeys={selected}
          onToggleRow={toggleRow}
          onToggleAll={toggleAll}
          enriched={enriched}
          enriching={enriching}
          columnsOpen={columnsOpen}
          onColumnsOpenChange={setColumnsOpen}
          loading={firstSync}
          loadingMessage={
            syncing && progress
              ? `Loading… ${progress.done}/${progress.total} regions (${progress.count.toLocaleString()} companies)`
              : 'Loading…'
          }
        />
        </div>
      </div>
    </div>
  )
}
